import { inject } from '@angular/core';
import { CanActivateFn, Router, Routes } from '@angular/router';
import { catchError, map, of } from 'rxjs';
import { AccountSessionsService, AuthService, UserService, happenAuthRoutes, happenUserManagementRoutes } from '@happen/auth';

// The Auth hub has a cookie session without a product-scoped bearer token.
// Keep this guard local: tenant administration still requires a product bearer token.
const accountSessionGuard: CanActivateFn = () => {
  const auth = inject(AuthService);
  if (auth.isAuthenticated()) return true;
  const sessions = inject(AccountSessionsService);
  const router = inject(Router);
  return sessions.getUserSessions().pipe(
    map(() => true),
    catchError(() => of(router.createUrlTree(['/sign-in'])))
  );
};

const profileSessionGuard: CanActivateFn = () => {
  const users = inject(UserService);
  const router = inject(Router);
  return users.getCurrentUser().pipe(
    map(() => true),
    catchError(() => of(router.createUrlTree(['/sign-in'])))
  );
};

export const routes: Routes = [
  // Redirect root to sign-in
  { path: '', redirectTo: 'sign-in', pathMatch: 'full' },

  // Preserve callback/invitation links (including their query parameters).
  { path: 'my-access', redirectTo: 'app/dashboard', pathMatch: 'full' },

  // Auth routes — sign-in, session-expired, /auth/callback, /auth/select-tenant
  ...happenAuthRoutes,

  // Authenticated area — sidebar + topbar shell layout
  {
    path: 'app',
    loadComponent: () =>
      import('./core/shell/shell.component').then((m) => m.ShellComponent),
    children: [
      { path: '', redirectTo: 'dashboard', pathMatch: 'full' },
      ...happenUserManagementRoutes.map(route => route.path === 'account/security'
        ? { ...route, canActivate: [accountSessionGuard] }
        : route.path === 'account/profile'
          ? { ...route, canActivate: [profileSessionGuard] }
          : route),
      {
        path: 'dashboard',
        loadComponent: () =>
          import('./features/app/dashboard.component').then(
            (m) => m.DashboardComponent
          ),
      },
    ],
  },
];
